import React from "react";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ModalMessage,
  ButtonGroup,
  CancelButton,
  ConfirmButton,
  Row,
  IconWrapper,
} from "./ConfirmationModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ReactComponent as TrashSvg } from "../../assets/icons/delete.svg";
/* Inline hero icons per variant — no extra asset imports, so backwards-compat
   with existing call sites is guaranteed. */
const VARIANT_ICON = {
  danger: <TrashSvg />,
  warning: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 3.5 21 19.5H3L12 3.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M12 10v4.5M12 17v.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  success: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.5 12.5 10 18l9.5-11"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
  primary: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      <path
        d="M12 8v5M12 16v.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonType = "danger",
  isLoading = false,
}) => {
  useLockBodyScroll(isOpen);

  const variant = ["danger", "warning", "success", "primary"].includes(
    confirmButtonType,
  )
    ? confirmButtonType
    : "danger";

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3600 }}
    >
      <ModalContainer>
        {/* Close sits absolute top-right — Row kept as a positioning shell
            to preserve the named export while hosting only the button. */}
        <Row>
          <CloseButton onClick={handleCancel} aria-label="Close">
            <CloseSvg />
          </CloseButton>
        </Row>

        <ModalContent>
          {/* Hero icon — colored circular badge reflecting the action variant */}
          <IconWrapper $variant={variant}>
            {VARIANT_ICON[variant] || VARIANT_ICON.danger}
          </IconWrapper>

          <ModalTitle>{title}</ModalTitle>
          <ModalMessage>{message}</ModalMessage>

          <ButtonGroup>
            <CancelButton onClick={handleCancel} disabled={isLoading}>
              {cancelText}
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              $variant={variant}
              disabled={isLoading}
            >
              {isLoading ? (
                <l-bouncy size="20" stroke="2" speed="1" color="#fff" />
              ) : (
                confirmText
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default ConfirmationModal;
